import { FormEvent, useEffect, useMemo, useState } from "react"
import { useCreateExperimentMutation } from "~/ui-rtk/api/experimentApi"
import { Button } from "~/ui-rtk/components/ui/controls"
import Select from "~/ui-rtk/components/ui/controls/Select"
import Alert from "~/ui-rtk/components/ui/common/Alert"
import DatePicker from "~/ui-rtk/components/ui/controls/DatePicker"
import Card from "~/ui-rtk/components/ui/common/Card"
import { H3, P } from "~/ui-rtk/components/ui/typography"
import useDrawer from "~/ui-rtk/hooks/drawer"
import useDate from "~/ui-rtk/hooks/date"
import useExperimentPowerGrid from "~/ui-rtk/hooks/experiment-power"
import { toCurrency } from "~/ui-rtk/utils/number-utils"
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  CalendarIcon,
} from "@heroicons/react/24/outline"
import {
  describeControlShare,
  EXPERIMENT_CADENCE_OPTIONS,
  EXPERIMENT_CONTROL_SHARE_MAX,
  EXPERIMENT_CONTROL_SHARE_MIN,
  EXPERIMENT_CONTROL_SHARE_STEP,
  EXPERIMENT_CONTROL_SHARES,
  EXPERIMENT_DATA_SOURCE_OPTIONS,
  EXPERIMENT_DEFAULT_CONTROL_PERCENTAGE,
  EXPERIMENT_DEFAULT_DURATION_DAYS,
  EXPERIMENT_DURATION_OPTIONS,
  EXPERIMENT_MIN_DAYS_AHEAD,
  formatControlSplit,
  type ExperimentDurationDays,
} from "~/ui-rtk/constants/experiments"
import type {
  ExperimentCadence,
  ExperimentDataSource,
} from "~/ui-rtk/types/experiments"

const WEDNESDAY = 3

// The pipeline designs one cross-channel test (Meta, TikTok, YouTube) for
// US DMAs; countries are informational until the pipeline takes them.
const COUNTRIES = ["United States"]

const FIELD_CLASS =
  "h-11 rounded-lg border border-system-blue-600 bg-system-blue-700 px-3 text-sm text-white outline-none focus:border-system-blue-400"
// DaisyUI's `.select` pins `min-height: 3rem`, which beats `h-11`; zero it so
// the dropdowns line up with the 44px text/date fields above them.
const SELECT_CLASS = `min-h-0 ${FIELD_CLASS}`

const CONTROL_SPLIT_OPTIONS = EXPERIMENT_CONTROL_SHARES.map(pct => {
  const option = describeControlShare(pct)
  return {
    value: String(pct),
    label: option.recommended ? `${option.label} (recommended)` : option.label,
  }
})

export default function ExperimentsFormDrawer() {
  const dayjs = useDate()
  const { closeDrawer, isOpen } = useDrawer()

  const [name, setName] = useState("")
  const [startDate, setStartDate] = useState<Date | null>(null)
  const [durationDays, setDurationDays] = useState<ExperimentDurationDays>(
    EXPERIMENT_DEFAULT_DURATION_DAYS,
  )
  const [cadence, setCadence] = useState<ExperimentCadence>("daily")
  const [dataSource, setDataSource] = useState<ExperimentDataSource | "">("")
  const [plannedBudget, setPlannedBudget] = useState("")
  const [expectedIroas, setExpectedIroas] = useState("")
  const [controlPercentage, setControlPercentage] = useState(
    EXPERIMENT_DEFAULT_CONTROL_PERCENTAGE,
  )
  const [createExperiment, { isLoading }] = useCreateExperimentMutation()
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const power = useExperimentPowerGrid()

  // WithDrawer keeps the content mounted after the X / click-outside close
  // (only closeDrawer() clears it), so a reopened form would still show the
  // previous draft's name, dates and options. Reset whenever it closes.
  useEffect(() => {
    if (!isOpen) {
      setName("")
      setStartDate(null)
      setDurationDays(EXPERIMENT_DEFAULT_DURATION_DAYS)
      setCadence("daily")
      setDataSource("")
      setPlannedBudget("")
      setExpectedIroas("")
      setControlPercentage(EXPERIMENT_DEFAULT_CONTROL_PERCENTAGE)
      setErrorMessage(null)
    }
  }, [isOpen])

  const selectedOption = useMemo(
    () => describeControlShare(controlPercentage),
    [controlPercentage],
  )

  const estimatedEndDate = useMemo(() => {
    if (!startDate) {
      return null
    }
    return dayjs(startDate).add(durationDays, "day")
  }, [startDate, durationDays])

  // Experiments start on Wednesdays, at least a week out — the pipeline's
  // own default when no date is given.
  const isSelectableStartDate = (date: Date) =>
    date.getDay() === WEDNESDAY &&
    dayjs(date).isAfter(dayjs().add(EXPERIMENT_MIN_DAYS_AHEAD - 1, "day"))

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    if (!name || !startDate) {
      return // Don't submit if required fields are missing
    }

    try {
      await createExperiment({
        name,
        estimatedStartDate: startDate.toISOString(),
        estimatedEndDate: estimatedEndDate?.toISOString(),
        countries: COUNTRIES,
        testPercentage: 100 - controlPercentage,
        controlPercentage,
        durationDays,
        cadence,
        ...(dataSource ? { dataSource } : {}),
        ...(Number(plannedBudget) > 0
          ? { totalBudget: Number(plannedBudget) }
          : {}),
        ...(Number(expectedIroas) > 0
          ? { expectedIroas: Number(expectedIroas) }
          : {}),
      }).unwrap()

      closeDrawer()
    } catch (err) {
      setErrorMessage("Failed to create experiment. Please try again.")
      console.error("Failed to create experiment:", err)
    }
  }

  const renderPowerHint = (controlPercentage: number) => {
    if (power.isLoading) {
      return (
        <span className="text-xs text-basic-grey-inactive">
          Checking statistical power…
        </span>
      )
    }
    const estimate = power.lookup(durationDays, controlPercentage)
    if (!estimate) {
      return (
        <span className="text-xs text-basic-grey-inactive">
          No power estimate yet for {durationDays} days at {controlPercentage}%
          control
          {power.hasAny
            ? ""
            : " — the weekly power run has not covered this account"}
          .
        </span>
      )
    }
    return (
      <span className="text-xs text-system-green-300">
        Detects lift ≥ {toCurrency(estimate.minDetectableLift)} over{" "}
        {durationDays} days ({estimate.powerPct}% power,{" "}
        {estimate.confidencePct}% confidence)
      </span>
    )
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="grid w-full max-w-135 gap-6 text-left"
      aria-label="Experiment configuration form"
    >
      <header className="grid gap-1">
        <H3>Configure a new experiment</H3>
        <P className="text-basic-grey-inactive">
          Provide a name, choose a start date and test length, and select a
          control split. The end date is calculated for you and the test can be
          extended while live.
        </P>
      </header>

      <div className="grid gap-4">
        <label className="grid gap-2 text-sm font-medium text-white">
          Experiment name
          <input
            required
            value={name}
            onChange={event => setName(event.target.value)}
            className={FIELD_CLASS}
            placeholder="Q4 promo uplift"
          />
        </label>

        <div className="grid gap-2">
          <label className="text-sm font-medium text-white">
            Estimated start date
          </label>
          <div className="flex items-center bg-system-blue-700 rounded-lg px-2 h-9 border border-system-blue-600">
            <CalendarIcon className="w-4 h-4 mr-2 text-system-gray-400" />
            <DatePicker
              selected={startDate}
              onChange={date => setStartDate(date)}
              filterDate={isSelectableStartDate}
              dateFormat="MMM d, yyyy"
              placeholderText="Select a Wednesday"
              className="bg-transparent border-none outline-none p-0 text-sm w-full text-white placeholder-system-gray-500 cursor-pointer"
              calendarClassName="ui-rtk-date-picker"
              renderCustomHeader={({
                date,
                decreaseMonth,
                increaseMonth,
                prevMonthButtonDisabled,
                nextMonthButtonDisabled,
              }) => (
                <div className="flex items-center justify-between px-2">
                  <span className="text-md font-semibold">
                    {dayjs(date).format("MMMM YYYY")}
                  </span>
                  <div className="flex gap-1">
                    <button
                      onClick={decreaseMonth}
                      disabled={prevMonthButtonDisabled}
                      className="p-1 hover:bg-basic-blue/20 rounded-md disabled:opacity-50"
                    >
                      <ChevronLeftIcon className="w-4 h-4" />
                    </button>
                    <button
                      onClick={increaseMonth}
                      disabled={nextMonthButtonDisabled}
                      className="p-1 hover:bg-basic-blue/20 rounded-md disabled:opacity-50"
                    >
                      <ChevronRightIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            />
          </div>
          <P className="text-xs text-basic-grey-inactive">
            Experiments start on Wednesdays, at least{" "}
            {EXPERIMENT_MIN_DAYS_AHEAD} days out.
          </P>
        </div>

        <div className="grid gap-3 md:grid-cols-3">
          <label className="grid gap-2 text-sm font-medium text-white">
            Test length
            <Select
              value={String(durationDays)}
              onChange={event =>
                setDurationDays(
                  Number(event.target.value) as ExperimentDurationDays,
                )
              }
              options={EXPERIMENT_DURATION_OPTIONS.map(days => ({
                value: String(days),
                label: `${days} days`,
              }))}
              classes={{ select: SELECT_CLASS }}
            />
          </label>

          <label className="grid gap-2 text-sm font-medium text-white">
            Grain
            <Select
              value={cadence}
              onChange={event =>
                setCadence(event.target.value as ExperimentCadence)
              }
              options={EXPERIMENT_CADENCE_OPTIONS.map(option => ({
                value: option.value,
                label: option.label,
              }))}
              classes={{ select: SELECT_CLASS }}
            />
          </label>

          <label className="grid gap-2 text-sm font-medium text-white">
            Design revenue source
            <Select
              value={dataSource}
              onChange={event =>
                setDataSource(event.target.value as ExperimentDataSource | "")
              }
              options={EXPERIMENT_DATA_SOURCE_OPTIONS}
              classes={{ select: SELECT_CLASS }}
            />
          </label>
        </div>
        <P className="text-xs text-basic-grey-inactive">
          {
            EXPERIMENT_CADENCE_OPTIONS.find(option => option.value === cadence)
              ?.description
          }{" "}
          The revenue source is the dependent variable the geo split is
          optimized on; results are reported on every metric either way. Target
          countries: {COUNTRIES.join(", ")}.
        </P>

        <div className="grid gap-3 md:grid-cols-2">
          <label className="grid gap-2 text-sm font-medium text-white">
            <span>
              Planned test budget{" "}
              <span className="font-normal text-basic-grey-inactive">
                (optional)
              </span>
            </span>
            <input
              type="number"
              min="0"
              step="any"
              inputMode="decimal"
              value={plannedBudget}
              onChange={event => setPlannedBudget(event.target.value)}
              className={FIELD_CLASS}
              placeholder="25000"
            />
          </label>
          <label className="grid gap-2 text-sm font-medium text-white">
            <span>
              Expected incremental ROAS{" "}
              <span className="font-normal text-basic-grey-inactive">
                (optional)
              </span>
            </span>
            <input
              type="number"
              min="0"
              step="any"
              inputMode="decimal"
              value={expectedIroas}
              onChange={event => setExpectedIroas(event.target.value)}
              className={FIELD_CLASS}
              placeholder="2.0"
            />
          </label>
        </div>
        <P className="text-xs text-basic-grey-inactive">
          Once the design is ready, these are combined with its measured
          precision to tell you whether the budget can produce a lift the test
          would detect.
        </P>
      </div>

      <section className="grid gap-3">
        <H3>Choose size of control</H3>
        <P className="text-sm text-basic-grey-inactive">
          The control share is a percentage of revenue held out, not a count of
          markets — any {EXPERIMENT_CONTROL_SHARE_STEP}% step from{" "}
          {EXPERIMENT_CONTROL_SHARE_MIN}% to {EXPERIMENT_CONTROL_SHARE_MAX}%.
          Power estimates come from the account&apos;s latest power run — if a
          split looks underpowered, pick a longer test or a larger control
          before requesting the design.
        </P>
        <label className="grid gap-2 text-sm font-medium text-white md:max-w-[50%]">
          Control split
          <Select
            value={String(controlPercentage)}
            onChange={event => setControlPercentage(Number(event.target.value))}
            options={CONTROL_SPLIT_OPTIONS}
            classes={{ select: SELECT_CLASS }}
          />
        </label>
        <Card
          className="border border-basic-blue bg-basic-dark-blue"
          data-testid="control-split-summary"
        >
          <div className="flex h-full flex-col gap-3">
            <div className="flex items-start justify-between">
              <div className="grid gap-1">
                <span className="text-sm uppercase text-basic-grey-inactive">
                  Size of control
                </span>
                <span className="text-lg font-medium text-white">
                  {formatControlSplit(controlPercentage)}
                </span>
              </div>
              {selectedOption.recommended && (
                <span className="rounded-full border border-system-blue-500 px-3 py-1 text-xs font-semibold text-system-blue-200">
                  Recommended
                </span>
              )}
            </div>
            <P className="text-sm text-basic-grey-inactive">
              {selectedOption.description}
            </P>
            <div className="flex justify-between text-sm text-white">
              <span>Estimated end date</span>
              <span>
                {estimatedEndDate
                  ? estimatedEndDate.format("MMM D, YYYY")
                  : "Select start date"}
              </span>
            </div>
            {renderPowerHint(controlPercentage)}
          </div>
        </Card>
      </section>

      <footer className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        {errorMessage && <Alert variant="danger" content={errorMessage} />}
        <div className="flex gap-2 self-end md:self-auto">
          <Button
            type="button"
            onClick={closeDrawer}
            className="h-[40px] min-h-[40px] px-4 bg-system-blue-600 text-white"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={!name || !startDate || isLoading}
            className="h-[40px] min-h-[40px] px-4"
          >
            {isLoading ? "Saving..." : "Save experiment"}
          </Button>
        </div>
      </footer>
    </form>
  )
}
